import AgentData from './AgentData';
import MonitorData from './MonitorData';
import { ElevenAgentData } from '../ecdn11/interfaces';
import { PingData, PingResponse } from './probeUrls';
import { NetworkData } from './networkDataStorage';

export interface LocalityGatherDuration {
  '10': number;
  '11': number;
  ICE: number;
  total: number;
}

export const DEFAULT_DURATIONS = Object.freeze({
  '10': -1,
  '11': -1,
  ICE: -1,
  ping: -1,
  total: -1
});

export interface LocalityConstructor {
  agentData?: AgentData;
  eleven?: ElevenAgentData;
  gatherDuration?: LocalityGatherDuration;
  ice?: RTCIceCandidateInit[];
  monitorData?: MonitorData;
  probe?: PingData;
}

export interface LocalData {
  version: string;
  publicIp: string;
  host: string;
  fqdn: string;
}

export const generateMonitorData = (
  eleven: ElevenAgentData,
  data?: MonitorData
): MonitorData => {
  const monitorData = new MonitorData(data);
  if (eleven.publicIp) {
    monitorData.externalIPAddress = eleven.publicIp;
  }
  if (!eleven.interfaces) {
    return monitorData;
  }
  const i = Object.values(eleven.interfaces).find(value => {
    return 'true' == value.default;
  });
  if (i) {
    if (i.addresses && i.addresses.length > 0) {
      monitorData.internalIPAddress = i.addresses[0];
    }
    if (i.gateways && i.gateways.length > 0) {
      monitorData.gatewayIPAddress = i.gateways[0];
    }
  }
  return monitorData;
};

export interface ILocality {
  machineName?: string;
  agentVersion?: string;
  legacyAgentVersion?: string;
  internalIP?: string;
  internalMdns?: string;
  gatewayIP?: string;
  externalIP?: string;
  arenaGroup?: string;
  arenaId?: number;
  iceCandidates?: RTCIceCandidateInit[];
  gatherDuration: LocalityGatherDuration;
  probeUrls?: PingResponse[];
  hasProbed?: boolean;
}

export const isLocalityConstructor = (
  obj?: unknown
): obj is LocalityConstructor => {
  const data = obj as LocalityConstructor;
  return (
    data &&
    (!!data.agentData ||
      !!data.eleven ||
      !!data.ice ||
      !!data.monitorData ||
      !!data.probe)
  );
};

export default class LocalityKey implements ILocality {
  machineName?: string;
  agentVersion?: string;
  legacyAgentVersion?: string;
  internalIP?: string;
  internalMdns?: string;
  gatewayIP?: string;
  externalIP?: string;
  arenaGroup?: string;
  arenaId?: number;
  iceCandidates?: RTCIceCandidateInit[];
  gatherDuration: LocalityGatherDuration;
  probeUrls?: PingResponse[];
  hasProbed?: boolean;

  // If we have 11 agent data it takes precedence over 10 agent data
  constructor(data?: LocalityConstructor | ILocality) {
    if (!isLocalityConstructor(data)) {
      const obj = data || ({} as ILocality);
      this.agentVersion = obj.agentVersion;
      this.legacyAgentVersion = obj.legacyAgentVersion;
      this.machineName = obj.machineName;
      this.internalIP = obj.internalIP;
      this.gatewayIP = obj.gatewayIP;
      this.externalIP = obj.externalIP;
      this.arenaGroup = obj.arenaGroup;
      this.arenaId = obj.arenaId;
      this.iceCandidates = obj.iceCandidates;
      this.gatherDuration = obj.gatherDuration || { ...DEFAULT_DURATIONS };
      this.probeUrls = obj.probeUrls;
      this.hasProbed = obj.hasProbed;
      return;
    }
    const obj = data || ({} as LocalityConstructor);
    const eleven = obj.eleven || ({} as ElevenAgentData);
    const ad = new AgentData(obj.agentData);
    this.agentVersion = eleven.version;
    this.legacyAgentVersion = ad.version;
    this.machineName = eleven.host || ad.machine_name;
    const md = generateMonitorData(eleven, obj.monitorData);
    this.internalIP = md.internalIPAddress;
    this.gatewayIP = md.gatewayIPAddress;
    this.externalIP = md.externalIPAddress;
    this.arenaGroup = md.arenaGroup;
    this.arenaId = md.arenaId;
    this.iceCandidates = obj.ice;
    this.gatherDuration = obj.gatherDuration || { ...DEFAULT_DURATIONS };
    this.probeUrls = obj.probe && obj.probe.probeUrls;
    this.hasProbed = obj.probe && obj.probe.hasProbed;
  }

  mergeNetworkData(data?: NetworkData): void {
    if (!data) {
      return;
    }
    if (!this.internalIP) {
      this.internalIP = data.internalIp;
    }
    if (!this.externalIP) {
      this.externalIP = data.externalIp;
    }
    if (!this.internalMdns) {
      this.internalMdns = data.internalMdns;
    }
  }
}
